// Model advisories - security issues in AI models, kept by Govern360 staff. G360_K516
//
// Each active advisory is matched daily against every organisation's
// vendors: where a vendor runs on a listed model (recorded on the vendor,
// or seen in its usage), a finding is raised on that vendor with the
// vendor's fix and what the organisation's own agents on the model mean.
//
// WHAT BELONGS HERE. Issues from a named public source - a vendor security
// advisory, a CVE, an AI vulnerability database entry - with its link.
// An advisory raises findings in customer registers within a day, so a
// guess or a rumour does not belong here. Withdraw rather than delete: a
// withdrawn advisory raises nothing new, and findings already raised stay
// for the organisation to close.
//
// A failed read is said, not shown as an empty list; a refused change is
// said, not shown as done.
import { useEffect, useState } from 'react';
import { Cpu, Plus, X } from 'lucide-react';
import { listModelAdvisories, saveModelAdvisory } from '../lib/adminApi.js';
import { DataTable } from '@govern360/shared/components/datatable.jsx';

const SEVS = ['critical', 'high', 'medium', 'low'];
const SEV_PILL = { critical: 'p-dang', high: 'p-warn', medium: 'p-info', low: 'p-gray' };
const SEV_RANK = { critical: 4, high: 3, medium: 2, low: 1 };
const EMPTY = { title: '', severity: 'high', modelsText: '', summary: '', affected: '', published_at: '', source_name: '', source_url: '', vendor_fix: '', mitigation: '' };

const COLS = [
  { key: 'title', label: 'Advisory', render: (a) => <strong>{a.title}</strong> },
  { key: 'severity', label: 'Severity', sort: (a) => SEV_RANK[a.severity] || 0, render: (a) => <span className={'pill ' + (SEV_PILL[a.severity] || 'p-gray')}>{a.severity}</span> },
  { key: 'models', label: 'Models', value: (a) => (a.models || []).join(', ') },
  { key: 'status', label: 'Status', render: (a) => <span className={'pill ' + (a.status === 'active' ? 'p-succ' : 'p-gray')}>{a.status}</span> },
  { key: 'published_at', label: 'Published' },
  { key: 'source', label: 'Source', value: (a) => a.source_name || a.source_url || '',
    render: (a) => (a.source_url ? <a href={a.source_url} target="_blank" rel="noopener noreferrer">{a.source_name || 'link'}</a> : (a.source_name || null)) },
  { key: 'fix', label: 'Vendor fix', value: (a) => (a.vendor_fix ? 'published' : 'none published') },
  { key: 'updated_at', label: 'Updated', value: (a) => (a.updated_at ? String(a.updated_at).slice(0, 10) : '') },
];

export default function ModelAdvisoriesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    const { data, error } = await listModelAdvisories();
    setLoadErr(error ? (error.message || 'the read failed') : '');
    setRows(data);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function edit(a) {
    setMsg('');
    setForm(a ? { ...EMPTY, ...a, modelsText: (a.models || []).join(', '), published_at: a.published_at || '' } : { ...EMPTY });
  }
  async function save() {
    const models = String(form.modelsText || '').split(',').map((m) => m.trim()).filter(Boolean);
    if (!form.title.trim()) { setMsg('An advisory needs a title.'); return; }
    if (!models.length) { setMsg('Name at least one model it affects.'); return; }
    if (form.source_url && !/^https?:\/\//i.test(form.source_url.trim())) { setMsg('The source link must start with http:// or https://.'); return; }
    if (!form.source_name.trim() && !form.source_url.trim()) { setMsg('Name the source: an advisory raises findings in customer registers.'); return; }
    const row = {
      id: form.id, title: form.title.trim(), severity: form.severity, models,
      summary: form.summary.trim() || null, affected: form.affected.trim() || null, published_at: form.published_at || null,
      source_name: form.source_name.trim() || null, source_url: form.source_url.trim() || null,
      vendor_fix: form.vendor_fix.trim() || null, mitigation: form.mitigation.trim() || null,
    };
    setBusy(true);
    const { error } = await saveModelAdvisory(row);
    setBusy(false);
    if (error) { setMsg('Not saved: ' + (error.message || 'refused')); return; }
    setForm(null); setMsg('Saved. Matching organisations get a finding within a day.'); load();
  }
  async function setStatus(a, status) {
    const { error } = await saveModelAdvisory({ id: a.id, status });
    if (error) { setMsg('Not changed: ' + (error.message || 'refused')); return; }
    setMsg(status === 'withdrawn' ? 'Withdrawn: it raises nothing new; findings already raised stay.' : 'Reactivated.');
    load();
  }

  const field = (key, label, props) => (
    <div style={{ marginTop: 8 }}>
      <label className="field-l">{label}</label>
      {props && props.area
        ? <textarea className="inp" rows={2} value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
        : <input className="inp" type={(props && props.type) || 'text'} value={form[key] || ''} placeholder={(props && props.ph) || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />}
    </div>
  );

  return (
    <div>
      <div className="ph">
        <div>
          <h1 className="ph-h1">Model advisories</h1>
          <p className="ph-sub">Security issues in AI models. Each active advisory is matched daily against every organisation&rsquo;s
            vendors, and raises a finding where a vendor runs on a listed model. Enter only issues from a named public source.</p>
        </div>
        <div className="ph-cta"><button className="btn primary" onClick={() => edit(null)}><Plus size={16} />Add advisory</button></div>
      </div>
      {msg && <p style={{ fontSize: 12, color: 'var(--g-text-2)', margin: '0 0 10px' }}>{msg}</p>}
      {form && (
        <div className="card" data-testid="advisory-form" style={{ padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <strong style={{ flex: 1 }}>{form.id ? 'Edit advisory' : 'New advisory'}</strong>
            <button className="btn outline" style={{ height: 28 }} onClick={() => setForm(null)}><X size={14} /></button>
          </div>
          {field('title', 'Title')}
          <div style={{ marginTop: 8 }}>
            <label className="field-l">Severity</label>
            <select className="inp" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
              {SEVS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {field('modelsText', 'Models it affects (comma-separated; end with * for a family, e.g. gpt-4o*)', { ph: 'Claude Sonnet 4.5, gpt-4o*' })}
          {field('summary', 'What the issue is', { area: true })}
          {field('affected', 'Affected versions or deployments')}
          {field('published_at', 'Published', { type: 'date' })}
          {field('source_name', 'Source', { ph: 'e.g. Vendor security advisory, NVD, AVID' })}
          {field('source_url', 'Source link', { ph: 'https://...' })}
          {field('vendor_fix', 'The vendor\'s fix, if published (leave empty if none)', { area: true })}
          {field('mitigation', 'Mitigation while unfixed', { area: true })}
          <button className="btn primary" disabled={busy} style={{ marginTop: 12 }} onClick={save}>Save advisory</button>
        </div>
      )}
      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--g-text-2)' }}>Loading&hellip;</div>
      ) : loadErr ? (
        <div className="card g360-admin-err" style={{ padding: 24, borderLeft: '3px solid var(--g-danger)' }}>
          <p style={{ color: 'var(--g-danger)', fontSize: 14, margin: '0 0 8px' }}>Model advisories could not be read</p>
          <p style={{ fontSize: 13, color: 'var(--g-text-2)', margin: 0 }}>{loadErr}. This is not the same as there being none.{/model_advisory/.test(loadErr) ? ' Migration 0274 may not have been run.' : ''}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--g-text-2)', fontSize: 13 }}>
          <Cpu size={22} /><p style={{ margin: '8px 0 0' }}>No advisories yet. Nothing is raised in any register until one is added.</p>
        </div>
      ) : (
        <DataTable rows={rows} columns={COLS} storageKey="g360.admin.advisories.hiddenCols" name="model-advisories"
          placeholder="Filter advisories..." rowKey={(a) => a.id}
          actions={(a) => (
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <button className="btn outline" style={{ height: 26, fontSize: 12 }} onClick={() => edit(a)}>Edit</button>
              {a.status === 'active'
                ? <button className="btn outline" style={{ height: 26, fontSize: 12 }} onClick={() => setStatus(a, 'withdrawn')}>Withdraw</button>
                : <button className="btn outline" style={{ height: 26, fontSize: 12 }} onClick={() => setStatus(a, 'active')}>Reactivate</button>}
            </span>
          )} />
      )}
    </div>
  );
}
